import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { GaTracker } from "@/components/GaTracker";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import Landing from "@/pages/Landing";
import BrandScore from "@/pages/BrandScore";
import BookCall from "@/pages/BookCall";
import RescheduleCall from "@/pages/RescheduleCall";
import AuditResults from "@/pages/AuditResults";
import Prescription from "@/pages/Prescription";
import Acquisition from "@/portal/admin/Acquisition";
import AcquisitionLead from "@/portal/admin/AcquisitionLead";
import UniversalRecord from "@/portal/admin/UniversalRecord";
import DeliveryWorkspace from "@/portal/admin/DeliveryWorkspace";
import Automations from "@/portal/admin/Automations";
import EmailTemplates from "@/portal/admin/EmailTemplates";

const BrandAudit = lazy(() => import("@/pages/BrandAudit"));
const Login = lazy(() => import("@/portal/Login"));
const ForgotPassword = lazy(() => import("@/portal/PasswordReset").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/portal/PasswordReset").then((m) => ({ default: m.ResetPassword })));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const PlanDetail = lazy(() => import("@/pages/PlanDetail"));
const MethodPage = lazy(() => import("@/pages/MethodPage"));
const PlaybookPage = lazy(() => import("@/pages/PlaybookPage"));
const AdFunnel = lazy(() => import("@/pages/AdFunnel"));
const AdPreview = lazy(() => import("@/pages/AdPreview"));
const WorkbookSuccess = lazy(() => import("@/pages/WorkbookSuccess"));
const PortalLayout = lazy(() => import("@/portal/PortalLayout"));
const AdminLayout = lazy(() => import("@/portal/admin/AdminLayout"));
const Overview = lazy(() => import("@/portal/pages/Overview"));
const Project = lazy(() => import("@/portal/pages/Project"));
const ActionCenter = lazy(() => import("@/portal/pages/ActionCenter"));
const SiteSetup = lazy(() => import("@/portal/pages/SiteSetup"));
const DesignReview = lazy(() => import("@/portal/pages/DesignReview"));
const Revisions = lazy(() => import("@/portal/pages/Revisions"));
const Assets = lazy(() => import("@/portal/pages/Assets"));
const Documents = lazy(() => import("@/portal/pages/Documents"));
const Billing = lazy(() => import("@/portal/pages/Billing"));
const Messages = lazy(() => import("@/portal/pages/Messages"));
const Website = lazy(() => import("@/portal/pages/Website"));
const Analytics = lazy(() => import("@/portal/pages/Analytics"));
const ContentOS = lazy(() => import("@/portal/pages/ContentOS"));
const Profile = lazy(() => import("@/portal/pages/Profile"));
const CommandCenter = lazy(() => import("@/portal/admin/CommandCenter"));
const Manual = lazy(() => import("@/portal/admin/Manual"));
const ContentEngine = lazy(() => import("@/portal/admin/ContentEngine"));
const MetaCallback = lazy(() => import("@/portal/admin/MetaCallback"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const BlogAdmin = lazy(() => import("@/portal/admin/BlogAdmin"));
const Clients = lazy(() => import("@/portal/admin/Clients"));
const ClientDetail = lazy(() => import("@/portal/admin/ClientDetail"));
const Tasks = lazy(() => import("@/portal/admin/Tasks"));
const Team = lazy(() => import("@/portal/admin/Team"));
const Inbox = lazy(() => import("@/portal/admin/Inbox"));
const EbookLeads = lazy(() => import("@/portal/admin/EbookLeads"));
const EmailCenter = lazy(() => import("@/portal/admin/EmailCenter"));
const AdminMessages = lazy(() => import("@/portal/admin/AdminMessages"));
const FunnelAnalytics = lazy(() => import("@/portal/admin/FunnelAnalytics"));

const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#0A0C0F]">
    <Loader2 className="h-8 w-8 animate-spin text-[#C6FF00]" />
  </div>
);

function NoIndex({ children }) {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);
  return children;
}

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  return children;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: "#0E1013", border: "1px solid #1E2127", color: "#fff" } }} />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <GaTracker />
          <Suspense fallback={<FullLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/brand-audit" element={<Navigate to="/brand-score" replace />} />
            <Route path="/brand-score" element={<BrandScore />} />
            <Route path="/book" element={<BookCall />} />
            <Route path="/reschedule/:token" element={<NoIndex><RescheduleCall /></NoIndex>} />
            <Route path="/audit/results/:token" element={<NoIndex><AuditResults /></NoIndex>} />
            <Route path="/prescription/:token" element={<NoIndex><Prescription /></NoIndex>} />
            <Route path="/login" element={<NoIndex><LoginGate /></NoIndex>} />
            <Route path="/forgot-password" element={<NoIndex><ForgotPassword /></NoIndex>} />
            <Route path="/reset-password" element={<NoIndex><ResetPassword /></NoIndex>} />
            <Route path="/payment/success" element={<NoIndex><PaymentSuccess /></NoIndex>} />
            <Route path="/payment/cancel" element={<NoIndex><PaymentCancel /></NoIndex>} />
            <Route path="/pricing/:slug" element={<PlanDetail />} />
            <Route path="/method" element={<MethodPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/ads" element={<NoIndex><AdPreview /></NoIndex>} />
            <Route path="/workbook/success" element={<NoIndex><WorkbookSuccess /></NoIndex>} />
            <Route path="/playbook" element={<PlaybookPage />} />
            <Route path="/audit" element={<AdFunnel />} />
            <Route path="/ig" element={<AdFunnel />} />

            <Route path="/portal" element={<NoIndex><Protected role="client"><PortalLayout /></Protected></NoIndex>}>
              <Route index element={<Overview />} />
              <Route path="actions" element={<ActionCenter />} />
              <Route path="site-setup" element={<SiteSetup />} />
              <Route path="project" element={<Project />} />
              <Route path="design-review" element={<DesignReview />} />
              <Route path="revisions" element={<Revisions />} />
              <Route path="assets" element={<Assets />} />
              <Route path="documents" element={<Documents />} />
              <Route path="billing" element={<Billing />} />
              <Route path="messages" element={<Messages />} />
              <Route path="website" element={<Website />} />
              <Route path="content" element={<ContentOS />} />
              <Route path="meta/callback" element={<MetaCallback endpoint="/client/content/meta/connect" back="/portal/content" />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route path="/admin" element={<NoIndex><Protected role="admin"><AdminLayout /></Protected></NoIndex>}>
              <Route index element={<CommandCenter />} />
              <Route path="acquisition" element={<Acquisition />} />
              <Route path="acquisition/:id" element={<AcquisitionLead />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="record/:id" element={<UniversalRecord />} />
              <Route path="delivery/:id" element={<DeliveryWorkspace />} />
              <Route path="automations" element={<Automations />} />
              <Route path="email-templates" element={<EmailTemplates />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="team" element={<Team />} />
              <Route path="manual" element={<Manual />} />
              <Route path="content" element={<ContentEngine />} />
              <Route path="meta/callback" element={<MetaCallback />} />
              <Route path="articles" element={<BlogAdmin />} />
              <Route path="brand-audits" element={<Navigate to="/admin/inbox" replace />} />
              <Route path="leads" element={<Navigate to="/admin/inbox" replace />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="ebook-leads" element={<EbookLeads />} />
              <Route path="email-center" element={<EmailCenter />} />
              <Route path="funnels" element={<FunnelAnalytics />} />
              <Route path="messages" element={<AdminMessages />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <StickyMobileCTA />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
